import React, { cloneElement } from 'react';
import { getRem, transitionAnimation } from 'lib/core';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { INPUT_ICON_POSITIONS } from '../constants';

const inputIconSize = getRem('24px');
const inlineInputIconSize = getRem('14px');

/**
 * The cloned element is an svg, so non-svg attributes need to be transient props.
 * Reminder: theme is never transient.
 */
const StyledInputIcon = styled(({ icon, ...props }) => cloneElement(icon, props))`
  ${transitionAnimation('fill')}
  display: flex;
  fill: ${({ theme }) => theme.color.gray['600'].value};
  flex-shrink: 0;
  height: ${inputIconSize};
  padding-right: ${({ theme }) => theme.size.spacing.small.value};
  width: ${inputIconSize};
  ${({ $isDisabled, $isFocused, $isReadOnly, theme }) =>
    $isFocused &&
    !$isDisabled &&
    !$isReadOnly &&
    css`
      fill: ${theme.color.primary['500'].value};
    `}
  ${({ $hasError, $isDisabled, theme }) =>
    $hasError &&
    !$isDisabled &&
    css`
      fill: ${theme.color.system.negative[500].value};
    `}
    ${({ $hasError, $isInline }) =>
    $hasError &&
    $isInline &&
    css`
      height: ${inlineInputIconSize};
      width: ${inlineInputIconSize};
    `}
`;

const InputIcon = ({
  dataTestId = undefined,
  hasError = false,
  icon,
  isDisabled = false,
  isFocused = false,
  isInline = false,
  isReadOnly = false,
  position = INPUT_ICON_POSITIONS.TRAILING,
  ...other
}) => {
  return (
    <StyledInputIcon
      data-testid={dataTestId}
      $hasError={hasError}
      icon={icon}
      $isFocused={isFocused}
      $isInline={isInline}
      $position={position}
      {...other}
    />
  );
};

InputIcon.propTypes = {
  /** String to use for testing. If dataTestId has a value, 'data-testid="dataTestId-error-icon" will be added to InputIcon. */
  dataTestId: PropTypes.string,
  /** If true, adjusts label styling */
  hasError: PropTypes.bool,
  /** Icon to be displayed within input field */
  icon: PropTypes.node.isRequired,
  /** If true, use the disabled style */
  isDisabled: PropTypes.bool,
  /** If true, use the focused style */
  isFocused: PropTypes.bool,
  /** If true, input icon is inside inline input */
  isInline: PropTypes.bool,
  /** If true, use the read-only style */
  isReadOnly: PropTypes.bool,
  /** Sets the position of icon */
  position: PropTypes.oneOf(Object.values(INPUT_ICON_POSITIONS)),
};

export { InputIcon };
